import React, { useEffect, useState } from "react";
import { Table, Button, Space, Popconfirm, Modal, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EyeOutlined, DeleteOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { format } from "date-fns";

import {
	NotificationRecipient,
	RELATED_ENTITY_TYPE_PATIENT,
} from "../../../../types/communications/NotificationRecipient";
import type { ScheduledNotification } from "../../../../types/communications/NotificationTypes";
import { fetchScheduledNotifications, cancelScheduledNotification } from "../../../../api/communications/scheduledNotificationsApi";
import { useNotifications } from "../../../../context/NotificationContext";
import SectionCard from "../../../../components/shared/SectionCard";
import StatusTag from "../../../../components/shared/StatusTag";

const { Text } = Typography;

interface ScheduledNotificationsTabProps {
	recipient: NotificationRecipient;
	/**
	 * CU-86d44j2ac: the entity the schedule is filed under. Defaults to the
	 * patient value so the patient workflow is unchanged.
	 */
	relatedEntityType?: string;
}

const ScheduledNotificationsTab: React.FC<ScheduledNotificationsTabProps> = ({
	recipient,
	relatedEntityType = RELATED_ENTITY_TYPE_PATIENT,
}) => {
	const [notifications, setNotifications] = useState<ScheduledNotification[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedNotification, setSelectedNotification] = useState<ScheduledNotification | null>(null);
	const [detailModalOpen, setDetailModalOpen] = useState(false);
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		loadNotifications();
	}, [recipient, relatedEntityType]);

	const loadNotifications = async () => {
		setIsLoading(true);
		try {
			const { data } = await fetchScheduledNotifications({ related_entity_type: relatedEntityType, related_entity_id: String(recipient.id) });
			setNotifications(data);
		} catch {
			showError("Failed to load scheduled notifications");
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancel = async (id: number) => {
		try {
			await cancelScheduledNotification(id);
			showSuccess("Notification cancelled successfully");
			loadNotifications();
		} catch {
			showError("Failed to cancel notification");
		}
	};


	const columns: ColumnsType<ScheduledNotification> = [
		{ title: "Template", dataIndex: ["notification_template", "name"], key: "template_name", render: (text) => <Text strong>{text || "Unknown Template"}</Text> },
		{ title: "Subject", dataIndex: ["notification_template", "subject"], key: "subject", ellipsis: true },
		{
			title: "Scheduled For",
			dataIndex: "scheduled_for",
			key: "scheduled_for",
			sorter: (a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime(),
			render: (date: string) => (
				<Space>
					<ClockCircleOutlined />
					{format(new Date(date), "MMM dd, yyyy hh:mm a")}
				</Space>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			filters: [
				{ text: "Pending", value: "pending" },
				{ text: "Sent", value: "sent" },
				{ text: "Failed", value: "failed" },
				{ text: "Cancelled", value: "cancelled" },
			],
			onFilter: (value, record) => record.status === value,
			render: (status: string) => <StatusTag status={status} />,
		},
		{ title: "Sent At", dataIndex: "sent_at", key: "sent_at", render: (date: string) => (date ? format(new Date(date), "MMM dd, yyyy hh:mm a") : "—") },
		{
			title: "Actions",
			key: "actions",
			width: 100,
			render: (_, record) => (
				<Space>
					<Button
						type="text"
						icon={<EyeOutlined />}
						onClick={() => {
							setSelectedNotification(record);
							setDetailModalOpen(true);
						}}
					/>
					{record.status === "pending" && (
						<Popconfirm title="Cancel notification" description="Are you sure you want to cancel this scheduled notification?" onConfirm={() => handleCancel(record.id)} okText="Yes" cancelText="No">
							<Button type="text" danger icon={<DeleteOutlined />} />
						</Popconfirm>
					)}
				</Space>
			),
		},
	];

	return (
		<>
			<SectionCard noPadding>
				<Table columns={columns} dataSource={notifications} rowKey="id" loading={isLoading} size="small" pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [5, 10, 25] }} scroll={{ x: "max-content" }} />
			</SectionCard>

			<Modal
				title="Scheduled Notification Details"
				open={detailModalOpen}
				onCancel={() => setDetailModalOpen(false)}
				footer={[
					<Button key="close" onClick={() => setDetailModalOpen(false)}>
						Close
					</Button>,
				]}
				width={700}
				destroyOnClose
			>
				{selectedNotification && (
					<Space direction="vertical" style={{ width: "100%" }} size="middle">
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: "12px 0" }}>
							<div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Template Name
								</Text>
								<div>
									<Text strong>{selectedNotification.notification_template?.name || "Unknown"}</Text>
								</div>
							</div>
							<div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Status
								</Text>
								<div>
									<StatusTag status={selectedNotification.status} />
								</div>
							</div>
							<div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Recipient Email
								</Text>
								<div>
									<Text>{selectedNotification.recipient_email || "—"}</Text>
								</div>
							</div>
							<div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Recipient Phone
								</Text>
								<div>
									<Text>{selectedNotification.recipient_phone || "—"}</Text>
								</div>
							</div>
							<div>
								<Text type="secondary" style={{ fontSize: 12 }}>
									Scheduled For
								</Text>
								<div>
									<Text>{format(new Date(selectedNotification.scheduled_for), "MMM dd, yyyy hh:mm a")}</Text>
								</div>
							</div>
							{selectedNotification.sent_at && (
								<div>
									<Text type="secondary" style={{ fontSize: 12 }}>
										Sent At
									</Text>
									<div>
										<Text>{format(new Date(selectedNotification.sent_at), "MMM dd, yyyy hh:mm a")}</Text>
									</div>
								</div>
							)}
						</div>
						{selectedNotification.error_message && (
							<div style={{ padding: 12, background: "var(--mg-danger-bg)", border: "1px solid var(--mg-danger)", borderRadius: 6 }}>
								<Text type="danger">{selectedNotification.error_message}</Text>
							</div>
						)}
						{selectedNotification.data && (
							<div>
								<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
									Template Data
								</Text>
								<pre style={{ maxHeight: 200, overflow: "auto", fontSize: 12, background: "var(--mg-bg-subtle)", padding: 12, borderRadius: 6, margin: 0 }}>{JSON.stringify(selectedNotification.data, null, 2)}</pre>
							</div>
						)}
					</Space>
				)}
			</Modal>
		</>
	);
};

export default ScheduledNotificationsTab;
